import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Globe,
  Linkedin,
  Facebook,
  Instagram,
} from "lucide-react";
import { XIcon } from "./XIcon";

import React from "react";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const serviceLinks = [
    "Software Development",
    "AI & Machine Learning",
    "Data Services & Annotation",
    "Digital Media & Marketing",
    "Team Augmentation",
    "Blockchain Development",
  ];

  const companyLinks = [
    { name: "About Us", path: "/about" },
    { name: "Our Mission", path: "/about" },
    { name: "Leadership Team", path: "/about" },
    { name: "Careers", path: "/contact" },
    { name: "Press & Media", path: "/contact" },
    { name: "Partner Program", path: "/contact" },
  ];

  const resourceLinks = [
    { name: "Case Studies", path: "/portfolio" },
    { name: "White Papers", path: "/portfolio" },
    { name: "Blog", path: "/" },
    { name: "Documentation", path: "/" },
    { name: "Help Center", path: "/contact" },
    { name: "Contact Support", path: "/contact" },
  ];

  return (
    <footer className="bg-white text-brand-grey relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-primary via-brand-tertiary to-brand-primary"></div>
        <div className="absolute top-20 left-20 w-32 h-32 border border-brand-primary/20 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-24 h-24 border border-brand-primary/20 rounded-full"></div>
      </div>

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-6">
              <img
                src="/assets/logo-main.png"
                alt="Kolaborate Logo"
                className="h-14 w-auto"
              />
            </Link>

            <p className="text-gray-600 mb-6 max-w-md leading-relaxed">
              Honoring African excellence, creating global value, and building
              lasting relationships that transform lives and businesses across
              continents. Join us in making African BPO the future of work.
            </p>

            {/* Contact Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <a
                href="tel:+256772957990"
                className="group flex items-center p-3 rounded-xl hover:bg-brand-secondary/50 transition-colors"
              >
                <Phone
                  size={16}
                  className="mr-3 text-brand-primary group-hover:scale-110 transition-transform"
                />
                <span className="text-gray-600 text-sm">+256 772 957 990</span>
              </a>
              <a
                href="https://kolaborate.africa"
                className="group flex items-center p-3 rounded-xl hover:bg-brand-secondary/50 transition-colors"
              >
                <Globe
                  size={16}
                  className="mr-3 text-brand-tertiary group-hover:scale-110 transition-transform"
                />
                <span className="text-gray-600 text-sm">kolaborate.africa</span>
              </a>
              <div className="group flex items-center p-3 rounded-xl hover:bg-brand-secondary/50 transition-colors">
                <MapPin
                  size={16}
                  className="mr-3 text-brand-primary group-hover:scale-110 transition-transform"
                />
                <span className="text-gray-600 text-sm">Kampala, Uganda</span>
              </div>
            </div>

            {/* Social Links and Email */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <a
                href="mailto:support@kolaborate.co"
                className="group flex items-center p-3 rounded-xl hover:bg-brand-secondary/50 transition-colors"
              >
                <Mail
                  size={16}
                  className="mr-3 text-brand-primary group-hover:scale-110 transition-transform"
                />
                <span className="text-gray-600 text-sm">
                  support@kolaborate.co
                </span>
              </a>

              <div className="flex space-x-4">
                {[
                  {
                    icon: Linkedin,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://www.linkedin.com/company/kolaborate-platforms/",
                  },
                  {
                    icon: XIcon,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://twitter.com/kolaborate_p",
                  },
                  {
                    icon: Facebook,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://www.facebook.com/kolaborateplatforms",
                  },
                  {
                    icon: Instagram,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://www.instagram.com/kolaborateplatforms",
                  },
                ].map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 bg-gradient-to-r ${social.gradient} rounded-2xl flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-110 group`}
                  >
                    <social.icon
                      size={20}
                      className="text-white group-hover:scale-110 transition-transform"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-brand-grey flex items-center">
              <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
              Services
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((item, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-gray-500 hover:text-brand-primary transition-colors text-sm hover:translate-x-1 transform duration-200 block"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-brand-grey flex items-center">
              <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    className="text-gray-500 hover:text-brand-primary transition-colors text-sm hover:translate-x-1 transform duration-200 block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-brand-grey flex items-center">
              <div className="w-2 h-2 bg-brand-tertiary rounded-full mr-2"></div>
              Resources
            </h4>
            <ul className="space-y-3">
              {resourceLinks.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    className="text-gray-500 hover:text-brand-tertiary transition-colors text-sm hover:translate-x-1 transform duration-200 block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="border-t border-gray-200 bg-brand-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="mb-6 lg:mb-0 text-center lg:text-left">
              <h4 className="text-xl font-bold text-brand-grey mb-2 flex items-center justify-center lg:justify-start">
                <div className="w-3 h-3 bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-full mr-2 animate-pulse"></div>
                Join the African Excellence Movement
              </h4>
              <p className="text-gray-600 text-sm">
                Get insights on African talent, impact stories, and partnership
                opportunities delivered to your inbox
              </p>
            </div>
            <div className="flex w-full lg:w-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="px-6 py-3 rounded-l-2xl border border-gray-300 flex-grow lg:w-80 focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white text-brand-grey placeholder-gray-400"
              />
              <button className="bg-gradient-to-r from-brand-primary to-brand-tertiary text-white px-8 py-3 rounded-r-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 font-semibold">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-center">
              <p className="text-gray-500 text-sm">
                © {currentYear} Kolaborate Platforms. All rights reserved.
              </p>
              <div className="flex items-center space-x-3 text-sm">
                <span className="px-3 py-1 bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-full text-white text-xs font-semibold">
                  🇺🇬 Uganda HQ
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-500">🇰🇪 Kenya</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-500">🇷🇼 Rwanda</span>
              </div>
            </div>
            <div className="flex flex-wrap justify-center lg:justify-end space-x-6">
              {[
                "Privacy Policy",
                "Terms of Service",
                "Cookie Policy",
                "Security",
              ].map((item, index) => (
                <Link
                  key={index}
                  to="/contact"
                  className="text-gray-500 hover:text-brand-primary text-sm transition-colors hover:underline"
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
